import {
  Box,
  Button,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { LoadingButton } from "@mui/lab";
import { useTour } from "@reactour/tour";
import PermitPackageIndicators from "../indicators/PermitPackageIndicators";
import React, { useEffect, useState, useMemo } from "react";
import {
  handleDownloadInstructions,
  setModalsState,
  setPermitLoadings,
} from "../../../store/actions/uiActions/myPermitsActions";
import {
  getDocumentKey,
  getDocumentLookup,
  getPermitTypeData,
  isCurrentFormNOC,
  isPaisDoc,
  isScheduleOfValuesDoc,
} from "../../permitForms/permitFormHelpers";
import { isSubAdminEmail } from "../../../functions/helperFunctions";

const PermitPackageMiddleContent = ({
  currentLeftNotarized,
  currentLeftInProgress,
  setFileSizeModalOpen,
  selectedPermitType,
  isMobile = false,
  isReadOnlyPage = false,
  showDirectDownloadLink = false,
  display = "flex",
}) => {
  const noc_data = useSelector((state) => state.nocData.nocData);
  const nocCreatingStatus = useSelector((state) => state.nocData.pdfProgress);
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const subAdminDomains = useSelector(
    (state) => state.nocData?.subAdmins?.subAdminDomains,
  );
  const { activeTab, selectedRow, leftValue, instructionDownloading } =
    useSelector((state) => state.myPermitsComponent);
  const { cancelPermitLoading, createLoading, downloading, tempLoading } =
    useSelector((state) => state.myPermitsComponent.permits.loadings);

  const { isOpen } = useTour();

  const dispatch = useDispatch();

  const [currentNocStatus, setCurrentNocStatus] = useState(null);
  const [downloadLinkCopyFeedback, setDownloadLinkCopyFeedback] =
    useState(null);

  const currentPermitData = useMemo(
    () => getPermitTypeData(selectedRow, selectedPermitType),
    [selectedRow, selectedPermitType],
  );
  const currentDocument =
    currentPermitData?.selected_documents?.[leftValue] || null;
  const currentDocKey = getDocumentKey(currentDocument) || "";
  const { data: currentDocData } = getDocumentLookup(
    currentPermitData?.documents,
    currentDocument,
  );
  const isCurrentDocPais = isPaisDoc(currentDocument);

  const recording = selectedRow?.processing_permit?.recording || {};
  const activeUserEmail = (
    currentUser?.subuser?.email ||
    currentUser?.user?.email ||
    currentUser?.email ||
    ""
  )
    ?.toLowerCase()
    ?.trim();
  const isBranchManager = Boolean(
    currentUser?.user?.enterprise?.is_branch || currentUser?.user?.is_branch,
  );
  const isReadOnlySubuserPackage = Boolean(
    isBranchManager &&
      selectedRow?.email &&
      activeUserEmail &&
      selectedRow.email.toLowerCase().trim() !== activeUserEmail,
  );
  const isSovDoc = isScheduleOfValuesDoc(currentDocument);
  const isRecorded = Boolean(recording?.is_recorded || selectedRow?.recorded);

  const canViewDirectDownloadLink =
    currentUser?.user?.role === "admin" ||
    isSubAdminEmail(currentUser?.user?.email, subAdminDomains);
  const directDownloadUrl = useMemo(() => {
    if (
      !showDirectDownloadLink ||
      !canViewDirectDownloadLink ||
      !currentDocKey
    ) {
      return "";
    }

    const additionalPermit =
      selectedRow?.additional_permits?.[selectedPermitType];
    const permitStatusData =
      selectedPermitType &&
      selectedPermitType !== selectedRow?.permit_type &&
      additionalPermit
        ? Array.isArray(additionalPermit)
          ? additionalPermit[0]
          : additionalPermit
        : selectedRow;
    const finalDocuments =
      permitStatusData?.processing_permit?.notarization?.final_documents;
    const finalizedDocument = Array.isArray(finalDocuments)
      ? finalDocuments.find(
          (document) =>
            getDocumentKey(document) === currentDocKey && document?.pdfUrl,
        )
      : null;
    const isCurrentDocumentRecorded =
      isCurrentFormNOC(selectedRow, selectedPermitType, leftValue) &&
      Boolean(
        permitStatusData?.processing_permit?.recording?.is_recorded ||
          permitStatusData?.recorded,
      );
    const downloadUrl =
      (isCurrentDocumentRecorded && permitStatusData?.recording_download_url) ||
      finalizedDocument?.pdfUrl ||
      "";

    return typeof downloadUrl === "string" && /^https?:\/\//i.test(downloadUrl)
      ? downloadUrl
      : "";
  }, [
    showDirectDownloadLink,
    canViewDirectDownloadLink,
    currentDocKey,
    selectedRow,
    selectedPermitType,
    leftValue,
  ]);
  const currentDownloadLinkCopyStatus =
    downloadLinkCopyFeedback?.url === directDownloadUrl
      ? downloadLinkCopyFeedback?.status
      : "";

  const handleCopyDownloadLink = async () => {
    if (!directDownloadUrl) return;

    try {
      await navigator.clipboard.writeText(directDownloadUrl);
      setDownloadLinkCopyFeedback({
        url: directDownloadUrl,
        status: "success",
      });
    } catch (error) {
      setDownloadLinkCopyFeedback({ url: directDownloadUrl, status: "error" });
    }
  };

  const canDownloadCurrentDoc = Boolean(
    isSovDoc
      ? currentDocData?.is_created &&
          (currentDocData?.blob_path || currentDocData?.signed_url)
      : isRecorded || (currentDocData?.is_created && currentDocData?.pdfUrl),
  );

  const shouldDisableDownload = Boolean(instructionDownloading)
    ? true
    : !canDownloadCurrentDoc;
  const isDocumentCreated = Boolean(
    currentDocument?.created || currentDocData?.is_created,
  );
  const isDocumentActivelyCreating = Boolean(
    !isDocumentCreated &&
      (tempLoading ||
        ["In Queue", "In Progress"].includes(currentDocData?.status)),
  );

  useEffect(() => {
    if (selectedRow && nocCreatingStatus && nocCreatingStatus.length > 0) {
      const foundStatus = nocCreatingStatus.find(
        (item) => item.id === selectedRow?.task_id,
      );

      if (foundStatus) {
        setCurrentNocStatus(foundStatus);
      } else {
        setCurrentNocStatus(null);
      }
    }
  }, [nocCreatingStatus, selectedRow]);

  useEffect(() => {
    if (!downloadLinkCopyFeedback) return;

    const timeoutId = setTimeout(() => setDownloadLinkCopyFeedback(null), 3000);

    return () => clearTimeout(timeoutId);
  }, [downloadLinkCopyFeedback]);

  return (
    <>
      <Box
        className="step15"
        sx={{
          borderLeft: isMobile ? "none" : "4px solid #d3d3d3",
          ml: isMobile ? 0 : 1,
          mb: 1,
          flex: 1,
          height: "100%",
          display,
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
        }}
      >
        <Box sx={{ mt: 1, flexShrink: 0 }}>
          <Typography
            variant="h5"
            align="center"
            color="primary"
            px={2}
            mb={0}
            sx={{ fontSize: "22px" }}
          >
            {currentPermitData?.selected_documents?.[leftValue]?.label}
          </Typography>

          <Typography
            variant="body2"
            fontWeight="bold"
            align="center"
            color="primary"
            px={2}
            mb={0}
          >
            {selectedRow?.city
              ? selectedRow?.city
                  .replace(/_/g, " ")
                  .split(" ")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")
              : "County Specific"}{" "}
            - {selectedRow?.county}
          </Typography>
        </Box>
        {/* Status Indicators */}

        <Box sx={{ flexShrink: 0 }}>
          <PermitPackageIndicators />
        </Box>

        {/* END OF STATUS INDICATORS */}

        {/* MIDDLE CONTENT */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 0,
          }}
        >
          {activeTab === 1 && selectedRow?.assigned_package?.is_assigned ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
              }}
            >
              <Typography
                pt={5}
                sx={{ px: 3 }}
                variant="h5"
                align="center"
                color="warning"
              >
                Permit Package has been assigned to the contractor!
              </Typography>
            </Box>
          ) : isDocumentActivelyCreating ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h5"
                align="center"
                color="warning"
                sx={{ fontSize: "22px" }}
                pt={2}
                px={1}
              >
                Please wait while the document is being created
              </Typography>
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <img src="/img/gif/HI9M.gif" alt="loading" width={100} />
              </Box>
            </Box>
          ) : currentNocStatus && !currentNocStatus?.queue_data?.done ? (
            //? IN QUEUE //////////////////////////////////////////
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                color="warning"
                sx={{ fontSize: "22px" }}
                pt={1}
                px={2}
              >
                {currentNocStatus?.queue_data?.in_processing_window === false
                  ? "Your Notice of Commencement is currently in queue position:"
                  : "Please wait while the document is being created"}
              </Typography>
              {currentNocStatus?.queue_data?.in_processing_window === false && (
                <Typography
                  variant="h4"
                  align="center"
                  color="primary"
                  pt={1}
                  px={2}
                >
                  {currentNocStatus?.queue_data?.position}
                </Typography>
              )}
              <Box sx={{ textAlign: "center", mt: 1 }}>
                <img src="/img/gif/HI9M.gif" alt="loading" width={80} />
              </Box>
            </Box>
          ) : //? ///////////////////////////////////////////////////////////
          noc_data?.failed_counties?.includes(selectedRow?.county) &&
            selectedRow?.documents?.noc?.is_created === false &&
            selectedRow?.documents?.noc?.status === "failed" ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                color="error"
                pt={2}
                px={2}
              >
                {selectedRow?.county} Appraiser's website is temporarily
                unavailable. Once the website can be accessed, the NOC will be
                automatically generated.
              </Typography>
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <img src="/img/gif/HI9M.gif" alt="loading" width={80} />
              </Box>
            </Box>
          ) : currentDocData?.status === "failed" ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                pt={2}
                pb={2}
                px={1}
                color="error"
              >
                Based on the address you provided, we were unable to locate the
                property
              </Typography>
              {!isReadOnlyPage && (
                <Button
                  disabled={isReadOnlySubuserPackage}
                  onClick={() =>
                    dispatch(setModalsState({ permitDrawerOpen: true }))
                  }
                  sx={{ mt: 1, minWidth: "80%" }}
                  variant="contained"
                  color="warning"
                  endIcon={<EditIcon />}
                >
                  UPDATE DETAILS
                </Button>
              )}
            </Box>
          ) : currentDocData?.status === "more" ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                pt={2}
                pb={2}
                px={1}
                color="error"
              >
                There are more than one property addresses found for the given
                address
              </Typography>
              {!isReadOnlyPage && (
                <Button
                  disabled={isReadOnlySubuserPackage}
                  onClick={() =>
                    dispatch(setModalsState({ permitDrawerOpen: true }))
                  }
                  sx={{ mt: 1, minWidth: "80%" }}
                  variant="contained"
                  color="warning"
                  endIcon={<EditIcon />}
                >
                  UPDATE DETAILS
                </Button>
              )}
            </Box>
          ) : !currentPermitData?.selected_documents?.[leftValue]?.created ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                color="warning"
                sx={{ fontSize: "18px" }}
                pt={2}
                px={2}
                pb={1}
              >
                The document has not yet been created
              </Typography>
              {!isReadOnlyPage && (
                <LoadingButton
                  loading={createLoading}
                  disabled={cancelPermitLoading || isReadOnlySubuserPackage}
                  onClick={async () => {
                    if (!isOpen) {
                      dispatch(setPermitLoadings({ createLoading: true }));
                      await dispatch(
                        setModalsState({ permitDrawerOpen: true }),
                      );
                    } else {
                      return;
                    }
                  }}
                  sx={{ mt: 0, minWidth: "80%" }}
                  variant="contained"
                  color="success"
                  size="small"
                >
                  CREATE FORM
                </LoadingButton>
              )}
            </Box>
          ) : (
            currentPermitData?.selected_documents?.[leftValue]?.created && (
              <>
                <Box
                  sx={{
                    mx: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  {currentLeftNotarized && currentLeftInProgress && (
                    <>
                      <Typography
                        px={2}
                        pt={2}
                        mb={2}
                        align="center"
                        variant="h5"
                        sx={{
                          color: "#4caf50",
                          fontSize: "22px",
                        }}
                      >
                        Notarization has been initiated and is currently in
                        progress...
                      </Typography>
                      <Typography
                        px={2}
                        mb={0}
                        align="center"
                        variant="body1"
                        color="primary"
                      ></Typography>
                    </>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 1,
                      mt: 4,
                      px: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {!currentLeftNotarized && !isReadOnlyPage && (
                      <Button
                        disabled={
                          isReadOnlySubuserPackage ||
                          Boolean(instructionDownloading) ||
                          Boolean(downloading) ||
                          currentLeftInProgress
                        }
                        onClick={() => {
                          dispatch(setModalsState({ permitDrawerOpen: true }));
                        }}
                        sx={{ mt: 1, minWidth: "10rem", px: 0.8 }}
                        variant="contained"
                        color="warning"
                        endIcon={<EditIcon />}
                        size="small"
                      >
                        Update Document
                      </Button>
                    )}
                    <LoadingButton
                      disabled={shouldDisableDownload}
                      loading={
                        downloading ===
                        `${currentDocKey}${selectedRow?.task_id}`
                      }
                      onClick={() => {
                        setFileSizeModalOpen(true);
                      }}
                      sx={{ mt: 1, minWidth: "10rem", px: 0.8 }}
                      variant="contained"
                      color={currentLeftNotarized ? "success" : "primary"}
                      endIcon={<DownloadIcon />}
                      size="small"
                    >
                      Download Form
                    </LoadingButton>
                  </Box>
                  {directDownloadUrl && (
                    <Box
                      sx={{
                        mt: 2,
                        mx: "auto",
                        width: "calc(100% - 32px)",
                        maxWidth: 420,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          minWidth: 0,
                          p: 1.25,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          bgcolor: "grey.50",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 36,
                            height: 36,
                            flexShrink: 0,
                            borderRadius: 1.5,
                            bgcolor: "background.paper",
                            color: "primary.main",
                          }}
                        >
                          <LinkOutlinedIcon fontSize="small" />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", fontWeight: 600, mb: 0.25 }}
                          >
                            Direct download link
                          </Typography>
                          <Tooltip
                            title={
                              <Box sx={{ overflowWrap: "anywhere" }}>
                                {directDownloadUrl}
                              </Box>
                            }
                            arrow
                          >
                            <Link
                              href={directDownloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              underline="hover"
                              aria-label="Open direct download link in a new tab"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: "0.8125rem",
                              }}
                            >
                              {directDownloadUrl}
                            </Link>
                          </Tooltip>
                        </Box>
                        <Tooltip
                          title={
                            currentDownloadLinkCopyStatus === "success"
                              ? "Copied!"
                              : "Copy download link"
                          }
                          arrow
                        >
                          <IconButton
                            onClick={handleCopyDownloadLink}
                            size="small"
                            aria-label={
                              currentDownloadLinkCopyStatus === "success"
                                ? "Download link copied"
                                : "Copy direct download link"
                            }
                            sx={{
                              width: 36,
                              height: 36,
                              flexShrink: 0,
                              border: "1px solid",
                              borderColor:
                                currentDownloadLinkCopyStatus === "success"
                                  ? "success.main"
                                  : "divider",
                              borderRadius: 1.5,
                              bgcolor: "background.paper",
                              color:
                                currentDownloadLinkCopyStatus === "success"
                                  ? "success.main"
                                  : "primary.main",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            {currentDownloadLinkCopyStatus === "success" ? (
                              <CheckIcon fontSize="small" />
                            ) : (
                              <ContentCopyIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {currentDownloadLinkCopyStatus && (
                        <Typography
                          variant="caption"
                          color={
                            currentDownloadLinkCopyStatus === "success"
                              ? "success.main"
                              : "error.main"
                          }
                          role={
                            currentDownloadLinkCopyStatus === "success"
                              ? "status"
                              : "alert"
                          }
                          sx={{
                            display: "block",
                            mt: 0.5,
                            textAlign: "center",
                          }}
                        >
                          {currentDownloadLinkCopyStatus === "success"
                            ? "Download link copied."
                            : "Unable to copy the link. Please copy it manually."}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {isReadOnlySubuserPackage && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1, textAlign: "center" }}
                    >
                      Read-only: this package belongs to your subuser.
                    </Typography>
                  )}
                  {isCurrentDocPais &&
                    currentPermitData?.selected_documents?.[leftValue]
                      ?.created && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0,
                          mt: 0,
                        }}
                      >
                        <LoadingButton
                          disabled={Boolean(downloading)}
                          loading={
                            instructionDownloading ===
                            `${currentDocKey}inst${selectedRow?.task_id}`
                          }
                          onClick={() => dispatch(handleDownloadInstructions())}
                          sx={{ mt: 1, minWidth: "10rem" }}
                          color="primary"
                          endIcon={<UploadFileIcon />}
                          size="small"
                        >
                          Download Instructions
                        </LoadingButton>
                      </Box>
                    )}
                </Box>
              </>
            )
          )}
        </Box>
      </Box>
    </>
  );
};

export default React.memo(PermitPackageMiddleContent);
